import {AppSettings} from '../../config/app-setting';
import {Injectable} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {AnagraficaClientiResponse} from '../entities/AnagraficaClientiResponse';
import {UtilityService} from "./utility.service";
import { BaseResponse } from '../entities/base/BaseResponse';
import { ApiResponse } from '../entities/base/ApiResponse';
import { delay, Observable, of } from 'rxjs';
import { ExecuteCommand } from '../entities/ExecuteCommand';
import { HistorySearch } from '../entities/HistorySearch';

@Injectable({
  providedIn: 'root'
})
export class ApiService {
    constructor(private http: HttpClient,
                private utilityService: UtilityService ) {
  }

  // -------- STATIC DATA API -------------------------------

  async GetAnagraficaSociGetSingle(): Promise<AnagraficaClientiResponse> {
    try {
      const response = await fetch(AppSettings.apiUrl + '/ApiAnagraficaSoci/GetSingle', {
        method: 'GET',
        headers: this.utilityService.GetFetchHttpHeader().headers
      });

      if (!response.ok) {
        return {
          success: false,
            message: 'Accesso negato. Effettua l\'accesso corretto.',
          sociList: []
        };
      }

      const data: AnagraficaClientiResponse = await response.json();

      return data;
    } catch (error) {
      return {
        success: false,
        message: 'Accesso negato. Effettua l\'accesso corretto.',
        sociList: []
      };
    }
  }

  GetGatelockVanLiteProvider_GetDevices_Observable(): Observable<ApiResponse> {
    const headers = this.utilityService.GetFetchHttpHeader().headers;

    return this.http.get<ApiResponse>(
      AppSettings.apiUrl + '/GatelockVanLiteProvider/GetDevices',
      { headers }
    );
  }

  GetGatelockVanLiteProvider_GetHistory_Observable(search: HistorySearch): Observable<ApiResponse> {
    const headers = this.utilityService.GetFetchHttpHeader().headers;

    let params = new HttpParams();

    if (search.startDate)
      params = params.set('startDate', this.toLocalDateString(search.startDate));
    if (search.endDate)
      params = params.set('endDate', this.toLocalDateString(search.endDate));
    if (search.licensePlate)
      params = params.set('licensePlate', search.licensePlate);
    if (search.userEmail)
      params = params.set('userEmail', search.userEmail);

    return this.http.get<ApiResponse>(
      AppSettings.apiUrl + '/GatelockVanLiteHistoryRepository/GetHistory',
      { headers, params }
    );
  }

  toLocalDateString(date: Date | null): string {
    if (!date)
      return '';

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  async PostDeviceCommand_ExecuteCommand(executeCommand: ExecuteCommand) : Promise<void> {
    try {
      const response = await fetch(`${AppSettings.apiUrl}/DeviceCommand/ExecuteCommand`, {
        method: 'POST',
        headers: this.utilityService.GetFetchHttpHeader().headers,
        body: JSON.stringify(executeCommand)
      });

      if (!response.ok) {
        console.error('Errore HTTP:', response.status, response.statusText);
        throw new Error(`HTTP error ${response.status}: ${response.statusText}`);
      }

    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  private async PostData<TIn, TOut extends BaseResponse, TApiResponse>(
    params: WriteRequestParams<TIn, TOut, TApiResponse>): Promise<TOut> {
    try {
      const response = await fetch(params.url, {
        method: 'POST',
        headers: this.utilityService.GetFetchHttpHeader().headers,
        body: JSON.stringify(params.objectToBeWritten)
      });

      if (!response.ok) {
        console.error('Errore HTTP:', response.status, response.statusText);

        const result = new params.outputConstructor();
        result.success = false;
        result.message = 'Errore HTTP durante la richiesta.';
        return result;
      }

      const apiResponse: TApiResponse = await response.json();

      if (params.apiResponseValidator(apiResponse)) {
        const result = new params.outputConstructor();
        result.success = true;
        params.outputMutator(result, apiResponse);
        return result;
      } else {
        const result = new params.outputConstructor();
        result.success = false;
        result.message = `Record non inserito`;
        return result;
      }
    } catch (error) {
      console.error("Errore durante l'inserimento:", error);

      const result = new params.outputConstructor();
      result.success = false;
      result.message = 'Errore di connessione o server.';
      return result;
    }
  }

  private async PutData<TIn, TOut extends BaseResponse, TApiResponse>(
    params: WriteRequestParams<TIn, TOut, TApiResponse>): Promise<TOut> {
    try {
      const response = await fetch(params.url, {
        method: 'PUT',
        headers: this.utilityService.GetFetchHttpHeader().headers,
        body: JSON.stringify(params.objectToBeWritten)
      });

      if (!response.ok) {
        console.error('Errore HTTP:', response.status, response.statusText);

        const result = new params.outputConstructor();
        result.success = false;
        result.message = 'Errore HTTP durante la richiesta.';
        return result;
      }

      const rawResponseBody = await response.text();

      if (!rawResponseBody) {
        const result = new params.outputConstructor();
        result.success = true;
        return result;
      }

      const apiResponse: TApiResponse = JSON.parse(rawResponseBody);

      if (params.apiResponseValidator(apiResponse)) {
        const result = new params.outputConstructor();
        result.success = true;
        params.outputMutator(result, apiResponse);
        return result;
      } else {
        const result = new params.outputConstructor();
        result.success = false;
        result.message = `Record non aggiornato`;
        return result;
      }
    } catch (error) {
      console.error("Errore durante l'aggiornamento:", error);

      const result = new params.outputConstructor();
      result.success = false;
      result.message = 'Errore di connessione o server.';
      return result;
    }
  }

  private async DeleteData<TIn, TOut extends BaseResponse, TApiResponse>(
    params: WriteRequestParams<TIn, TOut, TApiResponse>): Promise<TOut> {
      try {
        const response = await fetch(params.url, {
          method: 'DELETE',
          headers: this.utilityService.GetFetchHttpHeader().headers
        });

        if (!response.ok) {
          console.error('Errore HTTP:', response.status, response.statusText);

          const result = new params.outputConstructor();
          result.success = false;
          result.message = 'Errore HTTP durante la richiesta.';
          return result;
        }

        const apiResponse: TApiResponse = await response.json();

        if (params.apiResponseValidator(apiResponse)) {
          const result = new params.outputConstructor();
          result.success = true;
          params.outputMutator(result, apiResponse);
          return result;
        } else {
          const result = new params.outputConstructor();
          result.success = false;
          result.message = `Record non rimosso`;
          return result;
        }

      } catch (error) {
        console.error("Errore durante la rimozione:", error);

        const result = new params.outputConstructor();
        result.success = false;
        result.message = 'Errore di connessione o server.';
        return result;
      }
    }
}

type WriteRequestParams<TIn, TOut extends BaseResponse, TApiResponse> = {
  url: string;
  objectToBeWritten: TIn;
  apiResponseValidator: (data: TApiResponse) => boolean;
  outputConstructor: new () => TOut;
  outputMutator: (output: TOut, data: TApiResponse) => void;
};
